import React from 'react';
import { Button, Card, Space, Steps, Typography } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import type { Quotation } from '@/services/quotation.service';
import { formatQuotationPrintIdentity } from '@/utils/quotationRevisionPolicy';
import type { ContractReview } from '@/services/contractReview.service';

const { Text } = Typography;

type Props = {
  quotation: Quotation;
  estimateLabel?: string | null;
  estimationRevision?: number | null;
  onOpenEstimate?: () => void;
  contractReviews?: ContractReview[];
  salesOrderNumber?: string | null;
  salesOrderId?: string | null;
  onOpenContractReview?: (id: string) => void;
  onOpenSalesOrder?: () => void;
  linkedSalesOrders?: Array<Record<string, unknown>>;
  onOpenLinkedSalesOrder?: (id: string) => void;
};

export const QuotationLineagePanel: React.FC<Props> = ({
  quotation,
  estimateLabel,
  estimationRevision,
  onOpenEstimate,
  contractReviews = [],
  salesOrderNumber,
  salesOrderId,
  onOpenContractReview,
  onOpenSalesOrder,
  linkedSalesOrders = [],
  onOpenLinkedSalesOrder,
}) => {
  const approvedCr = contractReviews.find(
    (r) => r.review_status === 'Approved' && !r.is_stale
  );
  const anyCr = contractReviews[0];

  const consolidationSos = linkedSalesOrders
    .map((row) => ({
      id: String(row.sales_order_id || row.id || ''),
      number: String(row.sales_order_number || row.order_number || ''),
    }))
    .filter((r) => r.id);

  const steps = [
    {
      title: 'Estimate',
      description: estimateLabel ? (
        <Space direction="vertical" size={0}>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={onOpenEstimate}>
            {estimateLabel}
          </Button>
          {estimationRevision != null ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              EST REV {estimationRevision}
            </Text>
          ) : null}
        </Space>
      ) : (
        '—'
      ),
    },
    {
      title: 'Quotation',
      description: formatQuotationPrintIdentity(
        quotation.quotation_number,
        quotation.revision_number
      ),
    },
    {
      title: 'Contract Review',
      description: approvedCr ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => onOpenContractReview?.(approvedCr.id)}
        >
          {approvedCr.review_number} ({approvedCr.review_status})
        </Button>
      ) : anyCr ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => onOpenContractReview?.(anyCr.id)}
        >
          {anyCr.review_number} ({anyCr.review_status})
        </Button>
      ) : (
        'Not created'
      ),
    },
    {
      title: 'Customer Order',
      description:
        salesOrderNumber && salesOrderId ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={onOpenSalesOrder}>
            {salesOrderNumber}
          </Button>
        ) : salesOrderId ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={onOpenSalesOrder}>
            Open Customer Order
          </Button>
        ) : quotation.is_converted_to_sales_order ? (
          'Converted'
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Card size="small" title={<><LinkOutlined /> Commercial Lineage</>}>
      <Steps direction="vertical" size="small" current={quotation.is_converted_to_sales_order ? 3 : approvedCr ? 2 : 1} items={steps} />
      {consolidationSos.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Linked customer orders
          </Text>
          <Space wrap size={4} direction="vertical">
            {consolidationSos.map((so) => (
              <Button
                key={so.id}
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => onOpenLinkedSalesOrder?.(so.id)}
              >
                {so.number || 'Customer Order'}
              </Button>
            ))}
          </Space>
        </div>
      ) : null}
      {quotation.is_converted_to_sales_order ? (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Created from {formatQuotationPrintIdentity(quotation.quotation_number, quotation.revision_number)}
        </Text>
      ) : null}
    </Card>
  );
};

export default QuotationLineagePanel;
